import React, { useState, useEffect } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { Plane, Globe, MapPin, Clock, DollarSign, Users, Award, CheckCircle, Shield, Target, BookOpen, ChevronRight, Star, ArrowLeft, Calendar, TrendingUp } from 'lucide-react';
import courseContentData from '../data/courseContent.json';

// Icon mapping
const iconMap = {
  Plane, Globe, MapPin, Clock, DollarSign, Users, Award, 
  CheckCircle, Shield, Target, BookOpen, ChevronRight, 
  Star, ArrowLeft, Calendar, TrendingUp
};

const getIconComponent = (iconName, props = {}) => {
  const IconComponent = iconMap[iconName];
  return IconComponent ? <IconComponent {...props} /> : null;
};

const CourseTemplate = () => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const [hoveredBenefit, setHoveredBenefit] = useState(null);
  const [hoveredStep, setHoveredStep] = useState(null);

  // Extract courseId from path (e.g., /zero-to-hero -> zero-to-hero)
  const courseId = location.pathname.split('/').filter(Boolean)[0];

  // Get course data from JSON
  const courseData = courseContentData[courseId];

  useEffect(() => {
    setIsVisible(true);

    const handleScroll = () => {
      const sections = document.querySelectorAll('[data-section]');
      let currentSection = 'overview';

      sections.forEach(section => {
        const sectionTop = section.offsetTop;
        if (window.scrollY >= sectionTop - 120) {
          currentSection = section.id;
        }
      });

      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(sectionId);
    }
  };

  // If course not found, redirect to courses page
  if (!courseData) {
    return <Navigate to="/courses" replace />;
  }

  const { hero, overview, benefits, fleet, admission, sections } = courseData;

  const styles = {
    container: {
      minHeight: '100vh',
      background: '#ffffff',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      color: '#1e293b'
    },
    stickyHeader: {
      position: 'sticky',
      top: 0,
      zIndex: 100,
      background: 'rgba(255, 255, 255, 0.98)',
      backdropFilter: 'blur(20px)',
      borderBottom: '1px solid #e2e8f0',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
    },
    headerContent: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '16px 32px',
      display: 'flex',
      gap: '32px',
      alignItems: 'center',
      overflowX: 'auto'
    },
    navButton: {
      padding: '10px 20px',
      background: 'transparent',
      border: 'none',
      borderRadius: '8px',
      fontSize: '15px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.3s',
      color: '#64748b',
      whiteSpace: 'nowrap'
    },
    hero: {
      position: 'relative',
      background: 'linear-gradient(135deg, #001f3f 0%, #003d82 100%)',
      padding: '90px 32px 70px',
      overflow: 'hidden'
    },
    heroContent: {
      maxWidth: '1400px',
      margin: '0 auto',
      position: 'relative',
      zIndex: 1
    },
    heroGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '60px',
      alignItems: 'center'
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '10px',
      padding: '10px 20px',
      background: 'rgba(255, 255, 255, 0.15)',
      backdropFilter: 'blur(10px)',
      borderRadius: '100px',
      color: 'white',
      fontSize: '14px',
      fontWeight: '500',
      marginBottom: '32px',
      border: '1px solid rgba(255, 255, 255, 0.2)'
    },
    heroTitle: {
      fontSize: '56px',
      fontWeight: '700',
      color: 'white',
      lineHeight: '1.1',
      marginBottom: '24px'
    },
    heroAccent: {
      display: 'block',
      background: 'linear-gradient(135deg, #38bdf8, #7dd3fc)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },
    heroDescription: {
      fontSize: '18px',
      color: 'rgba(255, 255, 255, 0.9)',
      lineHeight: '1.7',
      marginBottom: '40px',
      maxWidth: '600px'
    },
    statsCard: {
      background: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(20px)',
      borderRadius: '24px',
      padding: '32px',
      border: '1px solid rgba(255, 255, 255, 0.2)'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '24px'
    },
    statItem: {
      display: 'flex',
      gap: '12px',
      alignItems: 'flex-start'
    },
    section: {
      padding: '80px 32px',
      maxWidth: '1400px',
      margin: '0 auto'
    },
    sectionHeader: {
      textAlign: 'center',
      marginBottom: '60px'
    },
    sectionTitle: {
      fontSize: '42px',
      fontWeight: '700',
      color: '#0f172a',
      marginBottom: '16px'
    },
    sectionDescription: {
      fontSize: '18px',
      color: '#64748b',
      maxWidth: '700px',
      margin: '0 auto',
      lineHeight: '1.7'
    },
    overviewGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr',
      gap: '32px',
      marginTop: '48px'
    },
    overviewCard: {
      background: 'white',
      padding: '32px',
      borderRadius: '16px',
      border: '1px solid #e2e8f0',
      transition: 'all 0.3s'
    },
    highlightCard: {
      background: 'linear-gradient(135deg, #0369a1 0%, #0284c7 100%)',
      color: 'white',
      padding: '32px',
      borderRadius: '16px'
    },
    benefitsSection: {
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      padding: '80px 32px'
    },
    benefitsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '32px',
      maxWidth: '1400px',
      margin: '0 auto'
    },
    benefitCard: {
      background: 'white',
      padding: '32px',
      borderRadius: '16px',
      border: '1px solid #e2e8f0',
      transition: 'all 0.3s',
      cursor: 'pointer'
    },
    fleetGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '24px',
      marginTop: '48px'
    },
    fleetCard: {
      background: 'white',
      padding: '24px',
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
      transition: 'all 0.3s'
    },
    admissionSteps: {
      display: 'grid',
      gap: '24px',
      marginTop: '48px'
    },
    stepCard: {
      background: 'white',
      padding: '32px',
      borderRadius: '16px',
      border: '1px solid #e2e8f0',
      display: 'grid',
      gridTemplateColumns: 'auto 1fr',
      gap: '24px',
      transition: 'all 0.3s',
      cursor: 'pointer'
    }
  };

  return (
    <div style={styles.container}>
      {/* Sticky Navigation */}
      <div style={styles.stickyHeader}>
        <div style={styles.headerContent}>
          {sections.map(section => (
            <button
              key={section.id}
              style={{
                ...styles.navButton,
                ...(activeSection === section.id ? {
                  color: '#0369a1',
                  background: '#e0f2fe',
                  fontWeight: '600'
                } : {})
              }}
              onClick={() => scrollToSection(section.id)}
            >
              {section.label}
            </button>
          ))}
        </div>
      </div>

      {/* Hero Section */}
      <section style={styles.hero}>
        <div style={styles.heroContent}>
          <div style={styles.heroGrid}>
            <div>
              <div style={styles.badge}>
                <Award size={16} />
                {hero.badge}
              </div>
              <h1 style={styles.heroTitle}>
                {hero.title}
                <span style={styles.heroAccent}>{hero.titleAccent}</span>
              </h1>
              <p style={styles.heroDescription}>{hero.description}</p>
            </div>
            <div style={styles.statsCard}>
              <div style={styles.statsGrid}>
                {hero.stats.map((stat, index) => (
                  <div key={index} style={styles.statItem}>
                    {getIconComponent(stat.icon, { className: 'stat-icon', size: 24 })}
                    <div>
                      <div style={{ fontSize: '20px', fontWeight: '700', color: 'white' }}>
                        {stat.value}
                      </div>
                      <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.8)' }}>
                        {stat.label}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Program Overview Section */}
      <section id="overview" data-section style={styles.section}>
        <div style={styles.sectionHeader}>
          <div className="section-badge">{overview.badge}</div>
          <h2 style={styles.sectionTitle}>{overview.title}</h2>
          <p style={styles.sectionDescription}>{overview.description}</p>
        </div>

        <div style={styles.overviewGrid}>
          {overview.cards.map((card, index) => (
            <div key={index} style={styles.overviewCard} className="overview-card">
              <div className="overview-icon-wrapper">
                {getIconComponent(card.icon, { className: 'overview-icon' })}
              </div>
              <h3 className="overview-card-title">{card.title}</h3>
              <div className="overview-value">{card.value}</div>
              <p className="overview-description">{card.description}</p>
            </div>
          ))}

          <div style={styles.highlightCard} className="highlight-card">
            <h3 className="highlight-title">Why This Program?</h3>
            <div className="highlight-list">
              {overview.highlights.map((highlight, index) => (
                <div key={index} className="highlight-item">
                  <CheckCircle className="highlight-check" size={20} />
                  <div>
                    <strong>{highlight.title}</strong>
                    <p>{highlight.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section id="benefits" data-section style={styles.benefitsSection}>
        <div style={styles.sectionHeader}>
          <div className="section-badge">{benefits.badge}</div>
          <h2 style={styles.sectionTitle}>{benefits.title}</h2>
          <p style={styles.sectionDescription}>{benefits.description}</p>
        </div>

        <div style={styles.benefitsGrid}>
          {benefits.items.map((benefit, index) => (
            <div
              key={index}
              style={styles.benefitCard}
              className="benefit-card"
              onMouseEnter={() => setHoveredBenefit(index)}
              onMouseLeave={() => setHoveredBenefit(null)}
            >
              <div
                className="benefit-icon-container"
                style={{
                  backgroundColor: hoveredBenefit === index ? benefit.accentColor : benefit.iconBg
                }}
              >
                {getIconComponent(benefit.icon, { className: 'benefit-icon' })}
              </div>
              <h3 className="benefit-title">{benefit.title}</h3>
              <p className="benefit-description">{benefit.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Fleet Section */}
      {fleet && (
        <section id="campus" data-section style={styles.section}>
          <div style={styles.sectionHeader}>
            <div className="section-badge">{fleet.badge}</div>
            <h2 style={styles.sectionTitle}>{fleet.title}</h2>
            <p style={styles.sectionDescription}>{fleet.description}</p>
          </div>

          <div style={styles.fleetGrid}>
            {fleet.items.map((item, index) => (
              <div key={index} style={styles.fleetCard} className="fleet-card">
                <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#0f172a', marginBottom: '8px' }}>
                  {item.name}
                </h3>
                <div style={{ fontSize: '14px', color: '#0369a1', fontWeight: '600', marginBottom: '12px' }}>
                  {item.type}
                </div>
                <p style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.6' }}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Admission Process Section */}
      <section id="admission" data-section style={styles.section}>
        <div style={styles.sectionHeader}>
          <div className="section-badge">{admission.badge}</div>
          <h2 style={styles.sectionTitle}>{admission.title}</h2>
          <p style={styles.sectionDescription}>{admission.description}</p>
        </div>

        <div style={styles.admissionSteps}>
          {admission.steps.map((step, index) => (
            <div
              key={index}
              style={styles.stepCard}
              className="step-card"
              onMouseEnter={() => setHoveredStep(index)}
              onMouseLeave={() => setHoveredStep(null)}
            >
              <div
                className="step-number"
                style={{
                  fontSize: '48px',
                  fontWeight: '700',
                  color: hoveredStep === index ? '#0369a1' : '#e2e8f0',
                  transition: 'color 0.3s'
                }}
              >
                {step.number}
              </div>
              <div>
                <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a', marginBottom: '8px' }}>
                  {step.title}
                </h3>
                <p style={{ fontSize: '16px', color: '#64748b', marginBottom: '12px' }}>
                  {step.description}
                </p>
                <p style={{ fontSize: '14px', color: '#94a3b8', lineHeight: '1.6' }}>
                  {step.detail}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <style>{`
        .section-badge {
          display: inline-block;
          padding: 8px 16px;
          background: linear-gradient(135deg, #e0f2fe, #dbeafe);
          color: #0369a1;
          border-radius: 100px;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 16px;
        }

        .overview-card:hover, .benefit-card:hover, .fleet-card:hover, .step-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
        }

        .benefit-icon-container {
          width: 64px;
          height: 64px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
          transition: all 0.3s;
        }

        .benefit-icon {
          color: white;
        }

        .benefit-title {
          font-size: 20px;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 12px;
        }

        .benefit-description {
          font-size: 15px;
          color: #64748b;
          line-height: 1.6;
        }

        .overview-icon-wrapper {
          width: 56px;
          height: 56px;
          background: linear-gradient(135deg, #e0f2fe, #dbeafe);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
        }

        .overview-icon {
          color: #0369a1;
        }

        .overview-card-title {
          font-size: 14px;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 8px;
        }

        .overview-value {
          font-size: 32px;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 12px;
        }

        .overview-description {
          font-size: 14px;
          color: #64748b;
          line-height: 1.6;
        }

        .highlight-title {
          font-size: 24px;
          font-weight: 700;
          color: white;
          margin-bottom: 24px;
        }

        .highlight-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .highlight-item {
          display: flex;
          gap: 12px;
          align-items: flex-start;
        }

        .highlight-check {
          color: #7dd3fc;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .highlight-item strong {
          display: block;
          font-size: 16px;
          margin-bottom: 4px;
        }

        .highlight-item p {
          font-size: 14px;
          opacity: 0.9;
          margin: 0;
        }

        @media (max-width: 1024px) {
          .heroGrid {
            grid-template-columns: 1fr !important;
          }
          
          .overviewGrid {
            grid-template-columns: 1fr !important;
          }
        }

        @media (max-width: 768px) {
          .heroTitle {
            font-size: 36px !important;
          }
          
          .sectionTitle {
            font-size: 32px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default CourseTemplate;
